import { useInterfaceDesign } from "../design/InterfaceDesign";
import { designTokens } from "../design/tokens";
import { DesignDialog as Dialog } from "../design/DesignDialog.js";
import {
  sceneObjectTypeInUse,
  duplicateSceneObjectType,
} from "../../../util/src/layout/scene-object-types";
import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LabelImportantIcon from "@mui/icons-material/LabelImportant";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import dico from "../../../util/src/language/dico.js";
import { getValueTypeColor } from "../../../util/src/blueprint/method.js";
import { getValidSceneAnimationDurationMs } from "../../../util/src/blueprint/runtime/scene-animations.js";
import {
  allocateUniqueCatalogName,
  isSceneCatalogEntryNameTaken,
  type SceneCatalogNameSection,
} from "../../../util/src/layout/scene-catalog-names.js";
import { createDefaultCustomEventTriggerGraph } from "../../../util/src/blueprint/trigger-graph.js";
import { createDefaultFunctionGraph } from "../../../util/src/blueprint/function-graph.js";
import { createDefaultVariableGraph } from "../../../util/src/blueprint/variable-graph.js";
import { getBehaviorCanvasViewportSize } from "./behaviorCanvasViewport.js";
import type {
  SceneBehaviorCatalog,
  SceneFunctionPort,
  BehaviorGraph,
} from "../display-editor/layout-types";
import type {
  SceneBehaviorRoute,
  SceneCatalogSection,
} from "./SceneBehaviorNavigator.js";

export interface SceneCatalogListCanvasProps {
  catalog: SceneBehaviorCatalog;
  section: SceneCatalogSection;
  layoutNodes?: unknown;
  onNavigate: (route: SceneBehaviorRoute) => void;
  onOpenNewTab?: (route: SceneBehaviorRoute) => void;
  setSceneCatalog?: (
    catalog:
      | SceneBehaviorCatalog
      | ((prev: SceneBehaviorCatalog) => SceneBehaviorCatalog),
  ) => void;
  readOnly?: boolean;
}

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function getAnimationDuration(graph: BehaviorGraph | undefined): string {
  const durationMs = getValidSceneAnimationDurationMs(graph);
  if (durationMs == null) return "-";
  return `${durationMs} ms`;
}

const ValueTypeDot = ({
  valueType,
  label,
  side = "left",
}: {
  valueType: string;
  label?: string;
  side?: "left" | "right";
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      flexDirection: side === "right" ? "row-reverse" : "row",
      maxWidth: "100%",
    }}
  >
    {valueType === "flow" ? (
      <LabelImportantIcon
        sx={{ fontSize: 16, color: "#111111", flexShrink: 0 }}
      />
    ) : (
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: getValueTypeColor(valueType),
          flexShrink: 0,
        }}
      />
    )}
    {label && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontSize: 11,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    )}
  </Box>
);

/**
 * Renders function signature ports in paired rows (input left, output right).
 */
const FunctionPortsPreview = ({
  inputs,
  outputs,
}: {
  inputs: SceneFunctionPort[];
  outputs: SceneFunctionPort[];
}) => {
  const rowCount = Math.max(inputs.length, outputs.length);
  if (rowCount === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.35,
        mt: 0.5,
        width: "100%",
      }}
    >
      {Array.from({ length: rowCount }, (_, index) => (
        <Box
          key={`fn-port-row-${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: 18,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            {inputs[index] ? (
              <ValueTypeDot
                valueType={inputs[index].valueType}
                label={inputs[index].label}
                side="left"
              />
            ) : null}
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {outputs[index] ? (
              <ValueTypeDot
                valueType={outputs[index].valueType}
                label={outputs[index].label}
                side="right"
              />
            ) : null}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const SceneCatalogListCanvasComponent: React.FC<
  SceneCatalogListCanvasProps
> = ({
  catalog,
  section,
  layoutNodes,
  onNavigate,
  onOpenNewTab,
  setSceneCatalog,
  readOnly = false,
}) => {
  const { mode, colorMode } = useInterfaceDesign();
  const d = (dico().behavior as Record<string, unknown>).sceneCatalog as
    | Record<string, unknown>
    | undefined;

  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    entryId: string;
  } | null>(null);

  const [renameDialog, setRenameDialog] = useState<{
    entryId: string;
    currentName: string;
  } | null>(null);
  const [renameName, setRenameName] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);

  const entries = getEntries(catalog, section);
  const emptyLabel =
    (d as Record<string, string> | undefined)?.[
      `empty${capitalize(section)}`
    ] ?? "";
  const createLabel =
    (d as Record<string, string> | undefined)?.create ?? "Create";
  const renameLabel =
    (d as Record<string, string> | undefined)?.rename ?? "Rename";
  const duplicateLabel =
    (d as Record<string, string> | undefined)?.duplicate ?? "Duplicate";
  const deleteLabel =
    (d as Record<string, string> | undefined)?.delete ?? "Delete";
  const nameEmptyError =
    (d as Record<string, string> | undefined)?.nameEmptyError ??
    "Name cannot be empty.";
  const nameTakenError =
    (d as Record<string, string> | undefined)?.nameTakenError ??
    (d as Record<string, string> | undefined)?.animationNameTakenError ??
    "An entry with this name already exists.";

  const handleCreate = useCallback(() => {
    if (!setSceneCatalog || readOnly) return;
    const id = generateId();
    const baseName = `${capitalize(section).slice(0, -1)} ${entries.length + 1}`;
    const name = allocateUniqueCatalogName(
      section as SceneCatalogNameSection,
      baseName,
      catalog,
    );
    const next = createEntry(catalog, section, id, name);
    setSceneCatalog(next);
    onNavigate({ level: "detail", section, entryId: id });
  }, [catalog, section, entries.length, setSceneCatalog, readOnly, onNavigate]);

  const handleCardClick = useCallback(
    (entryId: string, event: React.MouseEvent) => {
      const route: SceneBehaviorRoute = { level: "detail", section, entryId };
      if (event.metaKey || event.ctrlKey) {
        onOpenNewTab?.(route);
        return;
      }
      onNavigate(route);
    },
    [section, onNavigate, onOpenNewTab],
  );

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, entryId: string) => {
      event.stopPropagation();
      setMenuAnchor({ el: event.currentTarget, entryId });
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleRenameStart = useCallback(() => {
    if (!menuAnchor) return;
    const entry = entries.find((e) => e.id === menuAnchor.entryId);
    setRenameDialog({
      entryId: menuAnchor.entryId,
      currentName: entry?.name ?? "",
    });
    setRenameName(entry?.name ?? "");
    setRenameError(null);
    setMenuAnchor(null);
  }, [menuAnchor, entries]);

  const handleRenameDialogClose = useCallback(() => {
    setRenameDialog(null);
    setRenameError(null);
  }, []);

  const handleRenameConfirm = useCallback(() => {
    if (!renameDialog || !setSceneCatalog || readOnly) return;
    const trimmed = renameName.trim();
    if (!trimmed) {
      setRenameError(nameEmptyError);
      return;
    }
    if (
      isSceneCatalogEntryNameTaken(
        section as SceneCatalogNameSection,
        trimmed,
        catalog,
        { excludeEntryId: renameDialog.entryId },
      )
    ) {
      setRenameError(nameTakenError);
      return;
    }
    const next = renameEntry(catalog, section, renameDialog.entryId, trimmed);
    setSceneCatalog(next);
    handleRenameDialogClose();
  }, [
    catalog,
    handleRenameDialogClose,
    nameEmptyError,
    nameTakenError,
    readOnly,
    renameDialog,
    renameName,
    section,
    setSceneCatalog,
  ]);

  const handleDuplicate = useCallback(() => {
    if (!menuAnchor || !setSceneCatalog || readOnly) return;
    const next = duplicateEntry(catalog, section, menuAnchor.entryId);
    setSceneCatalog(next);
    setMenuAnchor(null);
  }, [menuAnchor, catalog, section, setSceneCatalog, readOnly]);

  const handleDelete = useCallback(() => {
    if (!menuAnchor || !setSceneCatalog || readOnly) return;
    const next = deleteEntry(catalog, section, menuAnchor.entryId);
    setSceneCatalog(next);
    setMenuAnchor(null);
  }, [menuAnchor, catalog, section, setSceneCatalog, readOnly]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor:
          mode === "modern" ? designTokens[colorMode].background : "#fafafa",
        overflow: "auto",
      }}
    >
      {!readOnly && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={handleCreate}
          >
            {createLabel}
          </Button>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          p: 3,
        }}
      >
        {entries.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
            {emptyLabel}
          </Typography>
        )}
        {entries.map((entry) => (
          <Paper
            key={entry.id}
            elevation={1}
            onClick={(event) => handleCardClick(entry.id, event)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 1.5,
              width: 360,
              cursor: "pointer",
              transition: "box-shadow 0.15s, transform 0.15s",
              userSelect: "none",
              "&:hover": {
                boxShadow: 4,
                transform: "translateY(-1px)",
              },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                {entry.name}
              </Typography>
              {entry.meta}
            </Box>
            {!readOnly && (
              <IconButton
                size="small"
                onClick={(event) => handleMenuOpen(event, entry.id)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
        ))}
      </Box>
      <Menu
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor?.el}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRenameStart}>{renameLabel}</MenuItem>
        <MenuItem onClick={handleDuplicate}>{duplicateLabel}</MenuItem>
        <MenuItem
          onClick={handleDelete}
          disabled={
            section === "objects" &&
            !!menuAnchor &&
            sceneObjectTypeInUse(catalog, menuAnchor.entryId, layoutNodes)
          }
          title={
            section === "objects"
              ? dico().behavior.sceneCatalog.objectEditor.inUse
              : undefined
          }
          sx={{ color: "error.main" }}
        >
          {deleteLabel}
        </MenuItem>
      </Menu>
      <Dialog
        open={Boolean(renameDialog)}
        onClose={handleRenameDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{renameLabel}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={renameName}
            error={Boolean(renameError)}
            helperText={renameError ?? " "}
            onChange={(event) => {
              setRenameName(event.target.value);
              if (renameError) setRenameError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleRenameConfirm();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameDialogClose}>
            {((dico() as Record<string, unknown>).cancel as string) ?? "Cancel"}
          </Button>
          <Button
            variant="contained"
            onClick={handleRenameConfirm}
            disabled={!renameName.trim()}
          >
            {((dico() as Record<string, unknown>).confirm as string) ?? "OK"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const SceneCatalogListCanvas = React.memo(
  SceneCatalogListCanvasComponent,
);

// ── Helpers ──────────────────────────────────────────────────────────────────

type ListEntry = { id: string; name: string; meta: React.ReactNode };

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getEntries(
  catalog: SceneBehaviorCatalog,
  section: SceneCatalogSection,
): ListEntry[] {
  switch (section) {
    case "animations":
      return catalog.animations.map((a) => ({
        id: a.id,
        name: a.name,
        meta: (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: 11 }}
          >
            {getAnimationDuration(a.graph)}
          </Typography>
        ),
      }));
    case "events":
      return catalog.customEvents.map((e) => ({
        id: e.id,
        name: e.name,
        meta: null as React.ReactNode,
      }));
    case "functions":
      return catalog.functions.map((f) => ({
        id: f.id,
        name: f.name,
        meta: <FunctionPortsPreview inputs={f.inputs} outputs={f.outputs} />,
      }));
    case "objects":
      return (catalog.objectTypes ?? []).map((o) => ({
        id: o.id,
        name: o.name,
        meta: (
          <Typography variant="caption">
            {o.fields.length} {dico().behavior.sceneCatalog.objectEditor.fields}
          </Typography>
        ),
      }));
    case "variables":
      return catalog.variables.map((v) => ({
        id: v.id,
        name: v.name,
        meta: (
          <>
            {v.inputs.map((p) => (
              <ValueTypeDot
                key={p.id}
                valueType={p.valueType}
                label={p.label}
              />
            ))}
            <ValueTypeDot
              valueType={v.outputValueType}
              label={
                catalog.objectTypes?.find((t) => t.id === v.outputObjectTypeId)
                  ?.name
              }
            />
          </>
        ),
      }));
  }
}

function createEntry(
  catalog: SceneBehaviorCatalog,
  section: SceneCatalogSection,
  id: string,
  name: string,
): SceneBehaviorCatalog {
  switch (section) {
    case "objects":
      return {
        ...catalog,
        objectTypes: [
          ...(catalog.objectTypes ?? []),
          { id, name, baseType: null, fields: [] },
        ],
      };
    case "animations":
      return {
        ...catalog,
        animations: [
          ...catalog.animations,
          { id, name, graph: { nodes: [], connections: [] } },
        ],
      };
    case "events": {
      const behaviorDico = dico().behavior;
      const { width, height } = getBehaviorCanvasViewportSize();
      return {
        ...catalog,
        customEvents: [
          ...catalog.customEvents,
          {
            id,
            name,
            triggerGraph: createDefaultCustomEventTriggerGraph({
              canvasWidth: width,
              canvasHeight: height,
              flowInPortLabel: behaviorDico.actions.flowIn,
            }),
          },
        ],
      };
    }
    case "functions": {
      const behaviorDico = dico().behavior;
      const { width, height } = getBehaviorCanvasViewportSize();
      return {
        ...catalog,
        functions: [
          ...catalog.functions,
          {
            id,
            name,
            inputs: [],
            outputs: [],
            graph: createDefaultFunctionGraph({
              canvasWidth: width,
              canvasHeight: height,
              flowOutPortLabel: behaviorDico.actions.flowOut,
              flowInPortLabel: behaviorDico.actions.flowIn,
            }),
          },
        ],
      };
    }
    case "variables": {
      const behaviorDico = dico().behavior;
      const { width, height } = getBehaviorCanvasViewportSize();
      return {
        ...catalog,
        variables: [
          ...catalog.variables,
          {
            id,
            name,
            outputValueType: "string",
            inputs: [],
            graph: createDefaultVariableGraph({
              canvasWidth: width,
              canvasHeight: height,
              valuePortLabel: behaviorDico.controls.value,
            }),
          },
        ],
      };
    }
  }
}

function renameEntry(
  catalog: SceneBehaviorCatalog,
  section: SceneCatalogSection,
  entryId: string,
  name: string,
): SceneBehaviorCatalog {
  switch (section) {
    case "objects":
      return {
        ...catalog,
        objectTypes: (catalog.objectTypes ?? []).map((o) =>
          o.id === entryId ? { ...o, name } : o,
        ),
      };
    case "animations":
      return {
        ...catalog,
        animations: catalog.animations.map((a) =>
          a.id === entryId ? { ...a, name } : a,
        ),
      };
    case "events":
      return {
        ...catalog,
        customEvents: catalog.customEvents.map((e) =>
          e.id === entryId ? { ...e, name } : e,
        ),
      };
    case "functions":
      return {
        ...catalog,
        functions: catalog.functions.map((f) =>
          f.id === entryId ? { ...f, name } : f,
        ),
      };
    case "variables":
      return {
        ...catalog,
        variables: catalog.variables.map((v) =>
          v.id === entryId ? { ...v, name } : v,
        ),
      };
  }
}

function duplicateEntry(
  catalog: SceneBehaviorCatalog,
  section: SceneCatalogSection,
  entryId: string,
): SceneBehaviorCatalog {
  const newId = generateId();
  const sectionKey = section as SceneCatalogNameSection;
  switch (section) {
    case "objects": {
      const src = catalog.objectTypes?.find((o) => o.id === entryId);
      if (!src) return catalog;
      return {
        ...catalog,
        objectTypes: [
          ...(catalog.objectTypes ?? []),
          duplicateSceneObjectType(
            src,
            newId,
            allocateUniqueCatalogName(
              sectionKey,
              src.name + " (copy)",
              catalog,
            ),
          ),
        ],
      };
    }
    case "animations": {
      const src = catalog.animations.find((a) => a.id === entryId);
      if (!src) return catalog;
      const name = allocateUniqueCatalogName(
        sectionKey,
        `${src.name} (copy)`,
        catalog,
      );
      return {
        ...catalog,
        animations: [
          ...catalog.animations,
          { ...structuredClone(src), id: newId, name },
        ],
      };
    }
    case "events": {
      const src = catalog.customEvents.find((e) => e.id === entryId);
      if (!src) return catalog;
      const name = allocateUniqueCatalogName(
        sectionKey,
        `${src.name} (copy)`,
        catalog,
      );
      return {
        ...catalog,
        customEvents: [
          ...catalog.customEvents,
          { ...structuredClone(src), id: newId, name },
        ],
      };
    }
    case "functions": {
      const src = catalog.functions.find((f) => f.id === entryId);
      if (!src) return catalog;
      const name = allocateUniqueCatalogName(
        sectionKey,
        `${src.name} (copy)`,
        catalog,
      );
      return {
        ...catalog,
        functions: [
          ...catalog.functions,
          { ...structuredClone(src), id: newId, name },
        ],
      };
    }
    case "variables": {
      const src = catalog.variables.find((v) => v.id === entryId);
      if (!src) return catalog;
      const name = allocateUniqueCatalogName(
        sectionKey,
        `${src.name} (copy)`,
        catalog,
      );
      return {
        ...catalog,
        variables: [
          ...catalog.variables,
          { ...structuredClone(src), id: newId, name },
        ],
      };
    }
  }
}

function deleteEntry(
  catalog: SceneBehaviorCatalog,
  section: SceneCatalogSection,
  entryId: string,
): SceneBehaviorCatalog {
  switch (section) {
    case "objects":
      return {
        ...catalog,
        objectTypes: (catalog.objectTypes ?? []).filter(
          (o) => o.id !== entryId,
        ),
      };
    case "animations":
      return {
        ...catalog,
        animations: catalog.animations.filter((a) => a.id !== entryId),
      };
    case "events":
      return {
        ...catalog,
        customEvents: catalog.customEvents.filter((e) => e.id !== entryId),
      };
    case "functions":
      return {
        ...catalog,
        functions: catalog.functions.filter((f) => f.id !== entryId),
      };
    case "variables":
      return {
        ...catalog,
        variables: catalog.variables.filter((v) => v.id !== entryId),
      };
  }
}
